import { useState, useEffect, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Sparkles, ChevronLeft, ChevronRight, Check } from "lucide-react";

const DEPARTMENTS = ["Men", "Women", "Kids", "Unisex"];
const GST_RATES = [0, 5, 12, 18, 28];
const SIZE_TYPES = ["UK", "EU", "US", "S / M / L / XL", "Numeric", "Free Size"];
const WARRANTIES = ["No Warranty", "Manufacturer Warranty", "Seller Warranty", "Brand Warranty"];

const STEPS = [
  { n: "01", key: "product", label: "Product" },
  { n: "02", key: "pricing", label: "Pricing" },
  { n: "03", key: "variants", label: "Variants" },
  { n: "04", key: "details", label: "Details" },
  { n: "05", key: "publish", label: "Publish" },
];

const EMPTY = {
  department: "", category: "", category_id: "", subcategory_id: "", category_name: "", subcategory_name: "",
  brand: "", title: "", description: "", sku: "", model_number: "",
  mrp: "", selling_price: "", gst_rate: 18, hsn_code: "",
  size_type: "", sizes: [], colors: [], stock: 0,
  material: "", care: "", highlights: [], benefits: [], country_of_origin: "India", manufacturer: "", manufacturer_address: "",
  weight_g: "", package_weight_g: "", length_cm: "", width_cm: "", height_cm: "",
  return_policy: "", returns_accepted: true, return_window_days: 7,
  replacement_available: false, warranty: "No Warranty", warranty_months: 0,
  images: [], colour_images: {}, video_url: "", status: "active", visibility: "visible",
};

const toBase64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

// Downscale + JPEG-compress in the browser so images stay small (base64 lives
// in the DB; a full phone photo can be several MB and blow the 16MB doc limit).
const compressImage = (file, maxDim = 1600, quality = 0.82) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
        else { width = Math.round((width * maxDim) / height); height = maxDim; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, width, height); // flatten any transparency
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// ---- Cloudinary (unsigned browser upload). Public cloud name + preset are safe
// to embed. When unset, images fall back to compressed base64 stored in the DB.
const CLOUDINARY_CLOUD = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const cloudinaryEnabled = () => !!(CLOUDINARY_CLOUD && CLOUDINARY_PRESET);
async function uploadToCloudinary(fileOrDataUrl, resourceType = "image") {
  const url = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/${resourceType}/upload`;
  const fd = new FormData();
  fd.append("file", fileOrDataUrl);
  fd.append("upload_preset", CLOUDINARY_PRESET);
  const r = await fetch(url, { method: "POST", body: fd });
  if (!r.ok) throw new Error(`Cloudinary ${r.status}`);
  const data = await r.json();
  return data.secure_url;
}

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function Label({ children, required }) {
  return <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{children}{required && <span className="text-[hsl(var(--accent))]"> *</span>}</label>;
}

function Chips({ items, onRemove }) {
  return items.map((s) => (
    <span key={s} className="px-2 py-1 border border-foreground text-xs font-mono flex items-center gap-2">
      {s} <button type="button" onClick={() => onRemove(s)}>×</button>
    </span>
  ));
}

export default function ProductWizard({ initial, onSaved, onClose }) {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiCareBusy, setAiCareBusy] = useState(false);
  const [aiBenefitsBusy, setAiBenefitsBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cats, setCats] = useState([]);
  const [sizeInput, setSizeInput] = useState("");
  const [colorInput, setColorInput] = useState("");
  const [hlInput, setHlInput] = useState("");

  const [attrs, setAttrs] = useState([]);
  useEffect(() => { api.get("/categories").then(r => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (f.category_id) api.get(`/categories/${f.category_id}/attributes`).then(r => setAttrs(r.data)).catch(() => setAttrs([]));
    else setAttrs([]);
  }, [f.category_id]);
  const setAttr = (key, val) => setF((s) => ({ ...s, attributes: { ...(s.attributes || {}), [key]: val } }));
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  // ---- Variant matrix: rebuild from sizes × colours, preserving edited rows ----
  const suggestSku = (size, colour) => [f.sku || "SKU", colour, size]
    .filter(Boolean).map((x) => String(x).replace(/\s+/g, "").toUpperCase()).join("-");
  useEffect(() => {
    if (f.sizes.length === 0 && f.colors.length === 0) return;
    const sizes = f.sizes.length ? f.sizes : [""];
    const colours = f.colors.length ? f.colors : [""];
    const combos = [];
    for (const s of sizes) for (const c of colours) {
      const existing = (f.variants || []).find((v) => v.size === s && v.colour === c);
      combos.push(existing || { size: s, colour: c, sku: suggestSku(s, c), stock: 0 });
    }
    setF((prev) => ({ ...prev, variants: combos }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.sizes, f.colors]);
  const updateVariant = (i, key, val) => setF((s) => ({ ...s, variants: s.variants.map((v, j) => j === i ? { ...v, [key]: val } : v) }));
  const totalStock = (f.variants || []).reduce((sum, v) => sum + (Number(v.stock) || 0), 0);
  const put = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const selectedCat = cats.find(c => c.id === f.category_id) || cats.find(c => c.slug === f.category);
  const subOptions = selectedCat?.subcategories || [];

  // ---- Pricing calculations ----
  const calc = useMemo(() => {
    const mrp = Number(f.mrp) || 0;
    const sp = Number(f.selling_price) || 0;
    const gst = Number(f.gst_rate) || 0;
    const discount = mrp > 0 && sp > 0 ? Math.round((1 - sp / mrp) * 100) : 0;
    const taxable = sp > 0 ? sp / (1 + gst / 100) : 0;
    const gstAmt = sp - taxable;
    return { mrp, sp, gst, discount, taxable, gstAmt };
  }, [f.mrp, f.selling_price, f.gst_rate]);

  // Compress each file, then upload to Cloudinary (URL) when configured,
  // otherwise keep the compressed base64 (stored in the DB as a fallback).
  const filesToRefs = async (files) => {
    const out = [];
    for (const fl of files) {
      const compressed = await compressImage(fl);
      out.push(cloudinaryEnabled() ? await uploadToCloudinary(compressed, "image") : compressed);
    }
    return out;
  };

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files).slice(0, 8 - f.images.length);
    setUploading(true);
    try { put("images", [...f.images, ...(await filesToRefs(files))]); }
    catch { toast.error("Image upload failed — please try again"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const colourImgs = (c) => (f.colour_images?.[c] || []);
  const uploadColourImages = async (colour, e) => {
    const existing = colourImgs(colour);
    const files = Array.from(e.target.files).slice(0, 8 - existing.length);
    setUploading(true);
    try { put("colour_images", { ...f.colour_images, [colour]: [...existing, ...(await filesToRefs(files))] }); }
    catch { toast.error("Image upload failed — please try again"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeColourImage = (colour, idx) => {
    put("colour_images", { ...f.colour_images, [colour]: colourImgs(colour).filter((_, j) => j !== idx) });
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { put("video_url", await uploadToCloudinary(file, "video")); toast.success("Video uploaded"); }
    catch { toast.error("Video upload failed — please try again"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const aiDescribe = async () => {
    if (!f.title.trim() && !selectedCat && !f.brand.trim()) {
      return toast.error("Add a product name, brand, or category first");
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/product-description", {
        title: f.title, brand: f.brand, department: f.department,
        category_name: selectedCat?.name || f.category_name || "",
        subcategory_name: f.subcategory_name, material: f.material,
        highlights: f.highlights, attributes: f.attributes,
      });
      put("description", data.description);
      toast.success("Description generated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI generation failed");
    }
    setAiBusy(false);
  };

  const aiCare = async () => {
    if (!selectedCat && !f.subcategory_name && !f.material.trim()) {
      return toast.error("Pick a category or add the material first");
    }
    setAiCareBusy(true);
    try {
      const { data } = await api.post("/ai/care-instructions", {
        title: f.title, department: f.department,
        category_name: selectedCat?.name || f.category_name || "",
        subcategory_name: f.subcategory_name, material: f.material,
        attributes: f.attributes,
      });
      put("care", data.care);
      toast.success("Care instructions generated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI generation failed");
    }
    setAiCareBusy(false);
  };

  // "Benefits You'll Feel" — AI drafts title+paragraph blocks from everything
  // the vendor already entered (specs, highlights, material, description).
  const aiBenefits = async () => {
    if (!f.title.trim() && !selectedCat && !f.material.trim() && !f.highlights.length) {
      return toast.error("Add a product name, category, material or highlights first");
    }
    setAiBenefitsBusy(true);
    try {
      const { data } = await api.post("/ai/benefits", {
        title: f.title, brand: f.brand, department: f.department,
        category_name: selectedCat?.name || f.category_name || "",
        subcategory_name: f.subcategory_name, material: f.material,
        highlights: f.highlights, attributes: f.attributes,
        description: f.description, care: f.care,
      });
      put("benefits", data.benefits || []);
      toast.success(`${(data.benefits || []).length} benefits drafted — edit as you like`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI generation failed");
    }
    setAiBenefitsBusy(false);
  };

  const setBenefit = (i, key, val) => put("benefits", f.benefits.map((b, j) => j === i ? { ...b, [key]: val } : b));

  const save = async (publish) => {
    if (!f.department) { setStep(0); return toast.error("Select a department"); }
    if (!f.category_id) { setStep(0); return toast.error("Select a category"); }
    if (!f.subcategory_id) { setStep(0); return toast.error("Select a subcategory"); }
    if (!f.brand.trim() || !f.title.trim()) { setStep(0); return toast.error("Brand and product name are required"); }
    if (!f.selling_price) { setStep(1); return toast.error("Selling price is required"); }
    if (!f.description.trim()) { setStep(3); return toast.error("Product description is required (Details step)"); }
    setBusy(true);
    try {
      const payload = {
        ...f,
        price: Number(f.selling_price) || Number(f.mrp) || 0,
        mrp: Number(f.mrp) || 0,
        selling_price: Number(f.selling_price) || 0,
        gst_rate: Number(f.gst_rate) || 0,
        stock: Number(f.stock) || 0,
        return_window_days: Number(f.return_window_days) || 0,
        warranty_months: Number(f.warranty_months) || 0,
        weight_g: Number(f.weight_g) || 0,
        package_weight_g: Number(f.package_weight_g) || 0,
        length_cm: Number(f.length_cm) || 0,
        width_cm: Number(f.width_cm) || 0,
        height_cm: Number(f.height_cm) || 0,
        status: publish ? "active" : "draft",
      };
      if (initial?.id) await api.put(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success(publish ? "Saved & published — awaiting inspection" : "Saved as draft");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-foreground/60 z-50 flex items-start justify-center overflow-auto p-4 md:p-10">
      <div className="bg-background w-full max-w-3xl border-2 border-foreground relative">
        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-7 pb-4 border-b border-foreground/20">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">The Spotters · Listing</p>
            <h3 className="font-serif text-3xl tracking-tighter">{initial ? "Edit Product" : "Add New Product"}</h3>
          </div>
          <button type="button" onClick={onClose}><X /></button>
        </div>

        {/* Step rail */}
        <div className="flex items-center gap-1 px-8 py-4 border-b border-foreground/20 overflow-x-auto">
          {STEPS.map((s, i) => (
            <button key={s.key} onClick={() => setStep(i)}
              className={`flex items-center gap-2 whitespace-nowrap font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${i === step ? "text-[hsl(var(--accent))]" : i < step ? "text-foreground" : "text-muted-foreground"}`}>
              <span className={`w-5 h-5 flex items-center justify-center border ${i === step ? "border-[hsl(var(--accent))]" : "border-foreground/30"}`}>
                {i < step ? <Check size={11} /> : s.n}
              </span>
              {s.label}
              {i < STEPS.length - 1 && <span className="mx-1 text-foreground/20">———</span>}
            </button>
          ))}
        </div>

        <div className="px-8 py-6 max-h-[60vh] overflow-y-auto">
          {/* STEP 01 — PRODUCT */}
          {step === 0 && (
            <div className="space-y-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Product Classification</p>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label required>Department</Label>
                  <select data-testid="pw-department" value={f.department} onChange={set("department")} className="underline-input w-full" required>
                    <option value="">Select department</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <Label required>Brand</Label>
                  <input data-testid="pw-brand" value={f.brand} onChange={set("brand")} placeholder="e.g. Adidas" className="underline-input w-full" required />
                </div>
                <div>
                  <Label required>Category</Label>
                  <select data-testid="pw-category" value={selectedCat?.id || ""}
                    onChange={(e) => { const c = cats.find(x => x.id === e.target.value); setF({ ...f, category_id: c?.id || "", category: c?.slug || "", category_name: c?.name || "", subcategory_id: "", subcategory_name: "" }); }}
                    className="underline-input w-full" required>
                    <option value="">Select category</option>
                    {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label required>Subcategory</Label>
                  <select data-testid="pw-subcategory" value={f.subcategory_id || ""} disabled={!selectedCat}
                    onChange={(e) => { const sub = subOptions.find(x => x.id === e.target.value); setF({ ...f, subcategory_id: sub?.id || "", subcategory_name: sub?.name || "" }); }}
                    className="underline-input w-full disabled:opacity-40" required>
                    <option value="">{selectedCat ? "Select subcategory" : "Pick a category first"}</option>
                    {subOptions.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                  </select>
                </div>
              </div>

              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] pt-2">Product Information</p>
              <div>
                <Label required>Product Name</Label>
                <input data-testid="pw-title" value={f.title} onChange={set("title")} placeholder="e.g. Adidas Runfalcon 5 Running Shoes" className="underline-input w-full" required />
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div><Label required>Seller SKU</Label><input data-testid="pw-sku" value={f.sku} onChange={set("sku")} placeholder="SPT-ADI-RF5-001" className="underline-input w-full" /></div>
                <div><Label>Brand / Model Number</Label><input value={f.model_number} onChange={set("model_number")} placeholder="IE8812" className="underline-input w-full" /></div>
              </div>
            </div>
          )}

          {/* STEP 02 — PRICING */}
          {step === 1 && (
            <div className="space-y-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Pricing & Tax</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border-2 border-foreground p-4">
                  <Label>MRP (₹)</Label>
                  <input data-testid="pw-mrp" type="number" value={f.mrp} onChange={set("mrp")} placeholder="4999" className="w-full bg-transparent font-serif text-3xl mt-1 outline-none" />
                </div>
                <div className="border-2 border-[hsl(var(--accent))] p-4">
                  <Label required>Selling Price (₹)</Label>
                  <input data-testid="pw-selling" type="number" value={f.selling_price} onChange={set("selling_price")} placeholder="3499" className="w-full bg-transparent font-serif text-3xl mt-1 outline-none text-[hsl(var(--accent))]" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label required>GST Rate</Label>
                  <select data-testid="pw-gst" value={f.gst_rate} onChange={(e) => put("gst_rate", Number(e.target.value))} className="underline-input w-full">
                    {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </div>
                <div><Label required>HSN Code</Label><input data-testid="pw-hsn" value={f.hsn_code} onChange={set("hsn_code")} placeholder="6404" className="underline-input w-full" /></div>
              </div>
              <div className="border border-foreground/30 p-5 font-mono text-sm space-y-2">
                {[
                  ["MRP", inr(calc.mrp)],
                  ["Selling Price", inr(calc.sp)],
                  ["Discount", calc.discount > 0 ? `${calc.discount}% OFF` : "—"],
                  ["Taxable Value", inr(calc.taxable)],
                  [`GST (${calc.gst}%)`, inr(calc.gstAmt)],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between text-muted-foreground"><span>{k}</span><span className="text-foreground">{v}</span></div>
                ))}
                <div className="flex justify-between border-t border-foreground/20 pt-2 mt-2 text-base">
                  <span className="uppercase tracking-widest text-[10px] self-center text-muted-foreground">Customer Price</span>
                  <span className="font-serif text-2xl text-[hsl(var(--accent))]">{inr(calc.sp)}</span>
                </div>
              </div>
            </div>
          )}

          {/* STEP 03 — VARIANTS */}
          {step === 2 && (
            <div className="space-y-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Inventory & Variants</p>
              <div className="max-w-xs">
                <Label>Size Type</Label>
                <select data-testid="pw-sizetype" value={f.size_type} onChange={set("size_type")} className="underline-input w-full">
                  <option value="">Select size type</option>
                  {SIZE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label>Available Sizes</Label>
                <div className="flex gap-2 flex-wrap my-2"><Chips items={f.sizes} onRemove={(s) => put("sizes", f.sizes.filter(x => x !== s))} /></div>
                <div className="flex gap-2 max-w-sm">
                  <input value={sizeInput} onChange={(e) => setSizeInput(e.target.value)} placeholder="e.g. UK 8" className="underline-input" />
                  <button type="button" onClick={() => { if (sizeInput) { put("sizes", [...f.sizes, sizeInput]); setSizeInput(""); } }} className="border px-3 text-xs font-mono uppercase">Add</button>
                </div>
              </div>
              <div>
                <Label>Colours</Label>
                <div className="flex gap-2 flex-wrap my-2"><Chips items={f.colors} onRemove={(c) => put("colors", f.colors.filter(x => x !== c))} /></div>
                <div className="flex gap-2 max-w-sm">
                  <input value={colorInput} onChange={(e) => setColorInput(e.target.value)} placeholder="e.g. Black" className="underline-input" />
                  <button type="button" onClick={() => { if (colorInput) { put("colors", [...f.colors, colorInput]); setColorInput(""); } }} className="border px-3 text-xs font-mono uppercase">Add</button>
                </div>
              </div>
              {(f.variants && f.variants.length > 0) ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Variant Inventory</Label>
                    <span className="font-mono text-[11px] uppercase tracking-widest">Total Stock: <span className="text-[hsl(var(--accent))]">{totalStock}</span> units</span>
                  </div>
                  <div className="border border-foreground/20 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-foreground/30 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                        <th className="text-left p-2">Size</th><th className="text-left p-2">Colour</th><th className="text-left p-2">SKU</th><th className="text-left p-2 w-24">Stock</th>
                      </tr></thead>
                      <tbody>
                        {f.variants.map((v, i) => (
                          <tr key={i} className="border-b border-foreground/10">
                            <td className="p-2 font-mono text-xs">{v.size || "—"}</td>
                            <td className="p-2 font-mono text-xs">{v.colour || "—"}</td>
                            <td className="p-2"><input data-testid={`var-sku-${i}`} value={v.sku} onChange={(e) => updateVariant(i, "sku", e.target.value)} className="w-full bg-transparent border-b border-foreground/20 font-mono text-xs py-1 outline-none focus:border-foreground" /></td>
                            <td className="p-2"><input data-testid={`var-stock-${i}`} type="number" value={v.stock} onChange={(e) => updateVariant(i, "stock", Number(e.target.value))} className="w-20 bg-transparent border-b border-foreground/20 font-mono text-xs py-1 outline-none focus:border-foreground" /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-2 font-mono text-[10px] text-muted-foreground">One row per size × colour. Edit each SKU and stock; the total is summed automatically.</p>
                </div>
              ) : (
                <div className="max-w-xs">
                  <Label required>Total Stock (units)</Label>
                  <input data-testid="pw-stock" type="number" value={f.stock} onChange={set("stock")} className="underline-input w-full" />
                  <p className="mt-2 font-mono text-[10px] text-muted-foreground">Add sizes and/or colours above to generate a per-variant inventory grid.</p>
                </div>
              )}
            </div>
          )}

          {/* STEP 04 — DETAILS */}
          {step === 3 && (
            <div className="space-y-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Product Details</p>
              {attrs.length > 0 && (
                <div className="border border-foreground/20 p-4 space-y-4">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{selectedCat?.name} Attributes</p>
                  <div className="grid md:grid-cols-2 gap-5">
                    {attrs.map((a) => {
                      const val = (f.attributes || {})[a.key];
                      return (
                        <div key={a.id}>
                          <Label required={a.required}>{a.name}</Label>
                          {a.type === "select" ? (
                            <select data-testid={`attr-${a.key}`} value={val || ""} onChange={(e) => setAttr(a.key, e.target.value)} className="underline-input w-full">
                              <option value="">Select {a.name.toLowerCase()}</option>
                              {a.options.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : a.type === "multiselect" ? (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {a.options.map((o) => {
                                const arr = Array.isArray(val) ? val : [];
                                const on = arr.includes(o);
                                return <button type="button" key={o} onClick={() => setAttr(a.key, on ? arr.filter((x) => x !== o) : [...arr, o])}
                                  className={`px-3 py-1.5 border font-mono text-[11px] uppercase ${on ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] border-[hsl(var(--accent))]" : "border-foreground/30"}`}>{o}</button>;
                              })}
                            </div>
                          ) : (
                            <input data-testid={`attr-${a.key}`} value={val || ""} onChange={(e) => setAttr(a.key, e.target.value)} className="underline-input w-full" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-5">
                <div><Label>Material</Label><input value={f.material} onChange={set("material")} placeholder="Mesh / Synthetic" className="underline-input w-full" /></div>
                <div><Label>Country of Origin</Label><input value={f.country_of_origin} onChange={set("country_of_origin")} className="underline-input w-full" /></div>
                <div><Label>Manufacturer</Label><input value={f.manufacturer} onChange={set("manufacturer")} className="underline-input w-full" /></div>
                <div><Label>Manufacturer Address</Label><input value={f.manufacturer_address} onChange={set("manufacturer_address")} className="underline-input w-full" /></div>
              </div>
              <div>
                <Label>Product Highlights</Label>
                <ul className="my-2 space-y-1">
                  {f.highlights.map((h, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm"><span className="text-[hsl(var(--accent))]">•</span> {h}
                      <button type="button" onClick={() => put("highlights", f.highlights.filter((_, j) => j !== i))} className="text-muted-foreground ml-1">×</button></li>
                  ))}
                </ul>
                <div className="flex gap-2 max-w-md">
                  <input value={hlInput} onChange={(e) => setHlInput(e.target.value)} placeholder="e.g. Breathable upper" className="underline-input" />
                  <button type="button" onClick={() => { if (hlInput) { put("highlights", [...f.highlights, hlInput]); setHlInput(""); } }} className="border px-3 text-xs font-mono uppercase flex items-center gap-1"><Plus size={12} /> Add</button>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Benefits You'll Feel</Label>
                  <button type="button" onClick={aiBenefits} disabled={aiBenefitsBusy}
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] hover:text-foreground disabled:opacity-50">
                    <Sparkles size={12} className={aiBenefitsBusy ? "animate-pulse" : ""} /> {aiBenefitsBusy ? "Writing…" : "Write with AI"}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-2">Short benefit blocks (title + a sentence) shown on the product page. Let AI draft them from your specs above, then edit freely.</p>
                <div className="space-y-3">
                  {f.benefits.map((b, i) => (
                    <div key={i} className="border border-foreground/20 p-3">
                      <div className="flex items-center gap-2">
                        <input value={b.title} onChange={(e) => setBenefit(i, "title", e.target.value)} placeholder="Benefit title — e.g. Made for Indian feet" className="underline-input flex-1 font-medium" />
                        <button type="button" onClick={() => put("benefits", f.benefits.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-foreground shrink-0" aria-label="Remove benefit">×</button>
                      </div>
                      <textarea value={b.body} onChange={(e) => setBenefit(i, "body", e.target.value)} placeholder="One or two sentences on the real-world benefit." className="underline-input w-full min-h-[56px] mt-2 text-sm" />
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => put("benefits", [...f.benefits, { title: "", body: "" }])} className="mt-2 border px-3 py-1.5 text-xs font-mono uppercase flex items-center gap-1"><Plus size={12} /> Add benefit</button>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label required>Product Description</Label>
                  <button type="button" onClick={aiDescribe} disabled={aiBusy}
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] hover:text-foreground disabled:opacity-50">
                    <Sparkles size={12} className={aiBusy ? "animate-pulse" : ""} /> {aiBusy ? "Writing…" : "Write with AI"}
                  </button>
                </div>
                <textarea data-testid="pw-description" value={f.description} onChange={set("description")} placeholder="Describe the product — or generate from the classification, material, attributes & highlights above" className="underline-input w-full min-h-[110px]" required />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Care Instructions</Label>
                  <button type="button" onClick={aiCare} disabled={aiCareBusy}
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] hover:text-foreground disabled:opacity-50">
                    <Sparkles size={12} className={aiCareBusy ? "animate-pulse" : ""} /> {aiCareBusy ? "Writing…" : "Write with AI"}
                  </button>
                </div>
                <textarea value={f.care} onChange={set("care")} placeholder="Wash, dry, storage & handling — or generate from the product type & material" className="underline-input w-full min-h-[80px]" />
              </div>

              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] pt-2">Shipping</p>
              <div className="grid md:grid-cols-2 gap-5">
                <div><Label>Product Weight (g)</Label><input type="number" value={f.weight_g} onChange={set("weight_g")} placeholder="850" className="underline-input w-full" /></div>
                <div><Label>Package Weight (g)</Label><input type="number" value={f.package_weight_g} onChange={set("package_weight_g")} placeholder="1100" className="underline-input w-full" /></div>
              </div>
              <div>
                <Label>Package Dimensions (cm)</Label>
                <div className="flex gap-3 mt-1">
                  <input type="number" value={f.length_cm} onChange={set("length_cm")} placeholder="L" className="underline-input" />
                  <input type="number" value={f.width_cm} onChange={set("width_cm")} placeholder="W" className="underline-input" />
                  <input type="number" value={f.height_cm} onChange={set("height_cm")} placeholder="H" className="underline-input" />
                </div>
              </div>

              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] pt-2">Returns & Warranty</p>
              <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={f.returns_accepted} onChange={(e) => put("returns_accepted", e.target.checked)} /> Accept returns</label>
              <div className="grid md:grid-cols-2 gap-5">
                <div><Label>Return Window (days)</Label><input type="number" disabled={!f.returns_accepted} value={f.return_window_days} onChange={set("return_window_days")} className="underline-input w-full disabled:opacity-40" /></div>
                <div>
                  <Label>Warranty</Label>
                  <select value={f.warranty} onChange={set("warranty")} className="underline-input w-full">{WARRANTIES.map(w => <option key={w} value={w}>{w}</option>)}</select>
                </div>
              </div>
              <div><Label>Return Conditions</Label><textarea value={f.return_policy} onChange={set("return_policy")} placeholder="Product must be unused and returned with original packaging and tags." className="underline-input w-full min-h-[60px]" /></div>
              <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={f.replacement_available} onChange={(e) => put("replacement_available", e.target.checked)} /> Replacement available</label>
            </div>
          )}

          {/* STEP 05 — MEDIA & PUBLISH */}
          {step === 4 && (
            <div className="space-y-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Product Media</p>
              <div>
                <Label>Images (up to 8 · first is primary)</Label>
                <div className="grid grid-cols-4 gap-2 my-2">
                  {f.images.map((src, i) => (
                    <div key={i} className="relative aspect-square bg-secondary">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      {i === 0 && <span className="absolute bottom-1 left-1 bg-foreground text-background px-1.5 py-0.5 font-mono text-[8px] uppercase">Primary</span>}
                      <button type="button" onClick={() => put("images", f.images.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-foreground text-background w-5 h-5 text-xs">×</button>
                    </div>
                  ))}
                </div>
                {f.images.length < 8 && <input data-testid="pw-images" type="file" multiple accept="image/*" onChange={uploadImages} disabled={uploading} className="text-xs font-mono disabled:opacity-40" />}
                {uploading && <p className="mt-1 font-mono text-[10px] text-[hsl(var(--accent))]">Uploading…</p>}
                <p className="mt-1 font-mono text-[10px] text-muted-foreground">Shown by default and as the cover. Per-colour images below override these when a buyer picks that colour.</p>
              </div>

              {f.colors.length > 0 && (
                <div>
                  <Label>Images by Colour</Label>
                  <p className="mb-3 font-mono text-[10px] text-muted-foreground">Upload a separate set for each colour — the storefront swaps to these when the buyer selects the colour.</p>
                  <div className="space-y-5">
                    {f.colors.map((c) => (
                      <div key={c} className="border border-foreground/20 p-4">
                        <p className="font-mono text-[11px] uppercase tracking-widest mb-2">{c}</p>
                        {colourImgs(c).length > 0 && (
                          <div className="grid grid-cols-4 gap-2 mb-2">
                            {colourImgs(c).map((src, i) => (
                              <div key={i} className="relative aspect-square bg-secondary">
                                <img src={src} alt="" className="w-full h-full object-cover" />
                                {i === 0 && <span className="absolute bottom-1 left-1 bg-foreground text-background px-1.5 py-0.5 font-mono text-[8px] uppercase">Primary</span>}
                                <button type="button" onClick={() => removeColourImage(c, i)} className="absolute top-1 right-1 bg-foreground text-background w-5 h-5 text-xs">×</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {colourImgs(c).length < 8 && <input type="file" multiple accept="image/*" onChange={(e) => uploadColourImages(c, e)} disabled={uploading} className="text-xs font-mono disabled:opacity-40" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label>Product Video</Label>
                <input data-testid="pw-video" value={f.video_url} onChange={set("video_url")} placeholder="YouTube / Vimeo / hosted video link" className="underline-input w-full" />
                {cloudinaryEnabled() ? (
                  <div className="mt-2">
                    <input type="file" accept="video/*" onChange={uploadVideo} disabled={uploading} className="text-xs font-mono disabled:opacity-40" />
                    <p className="mt-1 font-mono text-[10px] text-muted-foreground">Paste a link above, or upload a video file directly.</p>
                    {f.video_url && f.video_url.includes("res.cloudinary.com") && <p className="mt-1 font-mono text-[10px] text-[hsl(var(--accent))]">✓ Video uploaded</p>}
                  </div>
                ) : (
                  <p className="mt-1 font-mono text-[10px] text-muted-foreground">Paste a video link.</p>
                )}
              </div>

              <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] pt-2">Publishing</p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>Status</Label>
                  <div className="flex gap-2 mt-2">
                    {["draft", "active"].map(v => (
                      <button type="button" key={v} onClick={() => put("status", v)} className={`px-3 py-1.5 border font-mono text-[11px] uppercase ${f.status === v ? "bg-foreground text-background border-foreground" : "border-foreground/30"}`}>{v}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Visibility</Label>
                  <div className="flex gap-2 mt-2">
                    {["visible", "hidden"].map(v => (
                      <button type="button" key={v} onClick={() => put("visibility", v)} className={`px-3 py-1.5 border font-mono text-[11px] uppercase ${f.visibility === v ? "bg-foreground text-background border-foreground" : "border-foreground/30"}`}>{v}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between px-8 py-4 border-t border-foreground/20">
          <button type="button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest disabled:opacity-30"><ChevronLeft size={14} /> Back</button>
          <div className="flex gap-3">
            <button type="button" onClick={() => save(false)} disabled={busy || uploading} className="border border-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50">Save as Draft</button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={() => setStep(step + 1)} className="bg-foreground text-background px-6 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))] flex items-center gap-1">Next <ChevronRight size={14} /></button>
            ) : (
              <button data-testid="pw-publish" type="button" onClick={() => save(true)} disabled={busy || uploading} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-6 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50">{busy ? "Saving…" : uploading ? "Uploading…" : "Save & Publish"}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
